import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, Store,
  Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

// Simple chart components without external library
const SimpleBarChart = ({ data, maxValue, color = 'bg-orange-500' }) => {
  return (
    <div className="flex items-end gap-1 h-32">
      {data.map((item, index) => (
        <div key={index} className="flex-1 flex flex-col items-center">
          <div 
            className={`w-full ${color} rounded-t transition-all duration-500`}
            style={{ height: `${(item.value / maxValue) * 100}%`, minHeight: '4px' }}
          />
          <span className="text-xs text-slate-500 mt-1">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

const SimpleLineChart = ({ data, color = '#f97316' }) => {
  if (!data || data.length < 2) return null;
  
  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;
  
  const points = data.map((d, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - ((d.value - minValue) / range) * 100
  }));
  
  const pathD = points.map((p, i) => 
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');

  return (
    <div className="relative h-32 w-full">
      <svg viewBox="0 0 100 100" className="w-full h-full" preserveAspectRatio="none">
        <path
          d={pathD}
          fill="none"
          stroke={color}
          strokeWidth="2"
          vectorEffect="non-scaling-stroke"
        />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill={color} />
        ))}
      </svg>
      <div className="absolute bottom-0 left-0 right-0 flex justify-between text-xs text-slate-500">
        {data.map((d, i) => (
          <span key={i}>{d.label}</span>
        ))}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, change, changeType, icon: Icon, color }) => {
  const isPositive = changeType === 'increase' || (changeType === 'auto' && change >= 0);
  
  return (
    <Card className="dashboard-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-slate-500 mb-1">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                <span>{Math.abs(change)}% vs last period</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function AnalyticsDashboard() {
  const { api } = useAuth();
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    newCustomers: 0,
    avgOrderValue: 0,
    revenueChange: 0,
    ordersChange: 0,
    customersChange: 0,
    avgOrderChange: 0,
  });
  const [revenueData, setRevenueData] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [topRestaurants, setTopRestaurants] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch various data
      const [ordersRes, usersRes, restaurantsRes] = await Promise.all([
        api.get('/orders'),
        api.get('/users'),
        api.get('/restaurants')
      ]);

      const orders = ordersRes.data || [];
      const users = usersRes.data || [];
      const restaurants = restaurantsRes.data || [];

      // Calculate stats
      const totalRevenue = orders.reduce((sum, o) => sum + (o.total || 0), 0);
      const totalOrders = orders.length;
      const newCustomers = users.filter(u => u.role === 'customer').length;
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      setStats({
        totalRevenue,
        totalOrders,
        newCustomers,
        avgOrderValue,
        revenueChange: 12.5,
        ordersChange: 8.2,
        customersChange: 15.3,
        avgOrderChange: -2.1,
      });

      // Generate chart data (mock for now)
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 12;
      const labels = period === '12m' 
        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        : Array.from({ length: days }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (days - 1 - i));
            return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
          });

      setRevenueData(labels.map((label, i) => ({
        label: period === '12m' ? label : label.split(' ')[0],
        value: Math.floor(Math.random() * 500 + 100)
      })));

      setOrdersData(labels.map((label, i) => ({
        label: period === '12m' ? label : label.split(' ')[0],
        value: Math.floor(Math.random() * 20 + 5)
      })));

      // Top restaurants
      const restaurantOrders = {};
      orders.forEach(order => {
        if (!restaurantOrders[order.restaurant_id]) {
          restaurantOrders[order.restaurant_id] = { count: 0, revenue: 0 };
        }
        restaurantOrders[order.restaurant_id].count++;
        restaurantOrders[order.restaurant_id].revenue += order.total || 0;
      });

      const topRest = restaurants.map(r => ({
        ...r,
        orderCount: restaurantOrders[r.id]?.count || 0,
        revenue: restaurantOrders[r.id]?.revenue || 0
      })).sort((a, b) => b.revenue - a.revenue).slice(0, 5);

      setTopRestaurants(topRest);

      // Recent activity (mock)
      setRecentActivity([
        { type: 'order', message: 'New order from customer@hambre.app', time: '2 mins ago' },
        { type: 'user', message: 'New customer registered', time: '15 mins ago' },
        { type: 'review', message: 'New 5-star review for La Bella Italia', time: '1 hour ago' },
        { type: 'order', message: 'Order delivered successfully', time: '2 hours ago' },
      ]);

    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-6 animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-1/2 mb-2"></div>
              <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const maxRevenue = Math.max(...revenueData.map(d => d.value), 1);
  const maxOrders = Math.max(...ordersData.map(d => d.value), 1);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-40">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="12m">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Revenue"
          value={`£${stats.totalRevenue.toFixed(2)}`}
          change={stats.revenueChange}
          changeType="auto"
          icon={DollarSign}
          color="bg-green-500"
        />
        <StatCard
          title="Total Orders"
          value={stats.totalOrders}
          change={stats.ordersChange}
          changeType="auto"
          icon={ShoppingBag}
          color="bg-blue-500"
        />
        <StatCard
          title="New Customers"
          value={stats.newCustomers}
          change={stats.customersChange}
          changeType="auto"
          icon={Users}
          color="bg-purple-500"
        />
        <StatCard
          title="Avg Order Value"
          value={`£${stats.avgOrderValue.toFixed(2)}`}
          change={stats.avgOrderChange}
          changeType="auto"
          icon={TrendingUp}
          color="bg-orange-500"
        />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Revenue Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleBarChart data={revenueData} maxValue={maxRevenue} color="bg-green-500" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Orders Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <SimpleLineChart data={ordersData} color="#3b82f6" />
          </CardContent>
        </Card>
      </div>

      {/* Top Restaurants & Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Top Performing Restaurants</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topRestaurants.length > 0 ? (
                topRestaurants.map((restaurant, index) => (
                  <div key={restaurant.id} className="flex items-center gap-4">
                    <span className="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-sm font-medium">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{restaurant.name}</p>
                      <p className="text-sm text-slate-500">{restaurant.orderCount} orders</p>
                    </div>
                    <span className="font-semibold text-green-600">
                      £{restaurant.revenue.toFixed(2)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">No data available</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity.type === 'order' ? 'bg-blue-100 text-blue-600' :
                    activity.type === 'user' ? 'bg-purple-100 text-purple-600' :
                    'bg-yellow-100 text-yellow-600'
                  }`}>
                    {activity.type === 'order' ? <ShoppingBag className="w-4 h-4" /> :
                     activity.type === 'user' ? <Users className="w-4 h-4" /> :
                     <Store className="w-4 h-4" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-slate-900">{activity.message}</p>
                    <p className="text-xs text-slate-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
